import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Collapse, Typography, notification, Button, Modal } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import { casesService } from "../services/casesService";
import { CasesDTO } from "../models/Cases";
import AnagraficaPaziente from "../components/case-management/AnagraficaPaziente";
import NotizieCliniche from "../components/case-management/NotizieCliniche";
import Documenti from "../components/case-management/Documenti";
import Note from "../components/case-management/Note";
import { useAuth } from "../contexts/authContext";
import { useLoaderProvider } from "../contexts/LoaderContext";
import { patientsService } from "../services/patientsService";
import TitleTooltip from "../components/layout/TitleTooltip";
import styles from "./CaseManagement.module.css";

const { Panel } = Collapse;
const { Title } = Typography;

const CaseDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { showLoader, hideLoader } = useLoaderProvider();
  const [caseData, setCaseData] = useState<CasesDTO | null>(null);
  const [completedSections, setCompletedSections] = useState<string[]>([]);
  const [isSectionEditable, setIsSectionEditable] = useState<boolean>(false);
  const { loggedDoctor } = useAuth();

  useEffect(() => {
    const fetchCaseDetails = async () => {
      try {
        showLoader("Caricamento dei dettagli del caso...");
        const response = await casesService.getCaseById(Number(id));
        setCaseData(response);
        setIsSectionEditable(response.createdBy?.id === loggedDoctor?.id);
      } catch (error) {
        console.error("Errore nel caricamento dei dettagli del caso:", error);
        notification.error({
          message: "Errore nel caricamento del caso",
        });
      } finally {
        hideLoader();
      }
    };

    if (id) fetchCaseDetails();
  }, [hideLoader, id, loggedDoctor?.id, showLoader]);

  const handleSectionComplete = (section: string) => {
    if (!completedSections.includes(section)) {
      setCompletedSections((prev) => [...prev, section]);
    }
  };

  const isCompleted = (section: string) => completedSections.includes(section);

  const handlePatientPrivacyChange = (privacyConsent: boolean) => {
    if (!privacyConsent && caseData?.patient) {
      caseData.patient.privacyUploadDate = undefined;
    }

    if (privacyConsent && caseData?.patient) {
      caseData.patient.privacyUploadDate = new Date().toISOString();
    }

    setCaseData(caseData);
  };

  const renderPanelHeader = (
    title: string,
    descriptionTooltip: string,
    section: string
  ) => (
    <div className={styles.panelHeader}>
      <div className={styles.titleContainer}>
        <TitleTooltip
          title={title}
          tooltipText={descriptionTooltip}
          titleLevel={4}
          tooltipColor="#ff4d4f"
        />
      </div>
      {isCompleted(section) && (
        <CheckCircleFilled className={styles.checkIcon} />
      )}
    </div>
  );

  const onConfirmCase = async () => {
    const patientRefetched = await patientsService.getPatientById(
      caseData?.patient?.id!
    );
    if (!patientRefetched?.privacyUploadDate) {
      // mostrare errore se la privacy non è stata caricata
      Modal.error({
        title: "Consenso privacy non caricato",
        content: "Vai alla sezione documenti per caricare il consenso privacy",
      });
      return;
    }

    try {
      showLoader("Apertura del caso...");
      await casesService.changeStatus(Number(id), "OPEN");
      // Aggiorna lo stato del caso dopo la conferma
      const updatedCase = await casesService.getCaseById(Number(id));
      setCaseData(updatedCase);
      notification.success({ message: "Caso aperto con successo" });
    } catch (error) {
      console.error("Errore durante la conferma del caso:", error);
      notification.error({ message: "Errore durante l'apertura del caso" });
    } finally {
      hideLoader();
    }
  };

  const isConfirmButtonDisabled = caseData?.caseStatus !== "DRAFT";

  if (!caseData) return <></>;

  return (
    <div style={{ padding: "24px" }}>
      <Title level={2}>Dettagli Caso {caseData.id}</Title>
      <Collapse>
        <Panel
          header={renderPanelHeader(
            "Anagrafica Paziente",
            "Sezione dedicata alla creazione dell'anagrafica del pazinete",
            "anagrafica"
          )}
          key="1"
        >
          <AnagraficaPaziente
            onPatientUpdated={() => {}}
            patient={caseData.patient}
            isEditable={isSectionEditable}
          />
        </Panel>
        <Panel
          header={renderPanelHeader(
            "Documenti",
            "Sezione dedicata all'inserirmento dei documenti legati al caso",
            "documenti"
          )}
          key="2"
        >
          <Documenti
            patient={caseData.patient!}
            caseId={caseData.id!}
            onSave={() => handleSectionComplete("documenti")}
            setPatientPrivacy={(privacyConsens) =>
              handlePatientPrivacyChange(privacyConsens)
            }
          />
        </Panel>
        <Panel
          header={renderPanelHeader(
            "Notizie Cliniche",
            "Sezione dedicata all'inserimento delle notizie cliniche del paziente, anamnesi familiare e patologica",
            "notizieCliniche"
          )}
          key="3"
        >
          <NotizieCliniche
            caseId={caseData.id!}
            isEditable={isSectionEditable}
            onSectionComplete={() => handleSectionComplete("notizieCliniche")}
          />
        </Panel>
        <Panel
          header={renderPanelHeader(
            "Note",
            "Sezione dedicata allo scambio di messaggio ed informazioni tra i dottori che collaborano al caso",
            "note"
          )}
          key="4"
        >
          <Note caseDetail={caseData!} />
        </Panel>
      </Collapse>
      <div style={{ marginTop: "20px", textAlign: "center" }}>
        {caseData.caseStatus === "DRAFT" && (
          <Button
            type="primary"
            size="large"
            onClick={onConfirmCase}
            disabled={isConfirmButtonDisabled}
          >
            Conferma Apertura Caso
          </Button>
        )}
      </div>
    </div>
  );
};

export default CaseDetails;
